import { describeError, useApi } from '@classgo/app-kit';
import type { SetupStepId, SetupStepView } from '@classgo/shared';
import { Badge, Button, Card, EmptyState } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { useT } from '../../i18n';
import { setupApi, setupQueries } from '../../setup/api';

const tone = (status: SetupStepView['status']) =>
  status === 'done' ? 'success' : status === 'skipped' ? 'gray' : 'warning';

/**
 * The guided-setup checklist (ticket #67): every step with its state, the link to complete it
 * and a "we do not use this" mark; progress on top. `compact` hides done steps (the home card).
 */
export const SetupChecklist = ({ compact = false }: { compact?: boolean }) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const setup = useQuery(setupQueries.state(api));
  const toggle = useMutation({
    mutationFn: ({ step, skipped }: { step: SetupStepId; skipped: boolean }) =>
      skipped ? setupApi(api).unskip(step) : setupApi(api).skip(step),
    onSuccess: (state) => queries.setQueryData(setupQueries.state(api).queryKey, state),
  });
  if (setup.isError) {
    return (
      <Card title={t('setup.title')}>
        <EmptyState title={t('entity.error.title')} description={describeError(t, setup.error)} />
      </Card>
    );
  }
  if (setup.isPending) {
    return (
      <Card title={t('setup.title')}>
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      </Card>
    );
  }
  const state = setup.data;
  const steps = compact ? state.steps.filter((step) => step.status !== 'done') : state.steps;
  return (
    <Card title={t('setup.title')}>
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            {t('setup.progress', {
              done: state.progress.done + state.progress.skipped,
              total: state.progress.total,
            })}
          </span>
          <span className="font-medium text-gray-900">{state.progress.percent}%</span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={state.progress.percent}
          aria-valuemin={0}
          aria-valuemax={100}
          className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100"
        >
          <div
            className="h-2 rounded-full bg-brand-600"
            style={{ width: `${state.progress.percent}%` }}
          />
        </div>
        {state.ready ? <p className="mt-2 text-sm text-success-700">{t('setup.ready')}</p> : null}
      </div>
      <ol className="divide-y divide-gray-100">
        {steps.map((step) => (
          <li key={step.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Badge tone={tone(step.status)}>{t(`setup.status.${step.status}`)}</Badge>
                <span className="font-medium text-gray-900">{step.title}</span>
              </div>
              <p className="mt-1 text-sm text-gray-500">{step.description}</p>
            </div>
            <div className="flex items-center gap-2">
              {step.status !== 'done' ? (
                <Link
                  to={step.link as never}
                  className="text-sm text-brand-600 underline underline-offset-2 hover:decoration-2"
                >
                  {t('setup.go')}
                </Link>
              ) : null}
              {step.status !== 'done' || step.skipped ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggle.mutate({ step: step.id, skipped: step.skipped })}
                  disabled={toggle.isPending}
                >
                  {step.skipped ? t('setup.unskip') : t('setup.skip')}
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
      {compact && steps.length === 0 ? (
        <p className="text-sm text-gray-500">{t('setup.ready')}</p>
      ) : null}
    </Card>
  );
};
